<script setup lang="ts">
  import msApi from '@/utils/msApi';
  import { onMounted, onUnmounted, ref } from 'vue';

  const props = defineProps<{
    campaign_id: string;
  }>();

  const emits = defineEmits(['close-modal']);

  interface ReportInsight {
    platform: string;
    category: string;
    text: string;
    suggestion: string;
    buttonText: string;
  }

  interface ApiInsightData {
    Insight: string;
    Action: string;
    CTA: string;
  }

  const isFetchingInsights = ref(false);
  const reportInsights = ref<ReportInsight[]>();

  const campaignPerformanceAnalysis = {
    strong: [
      '8.3% engagement rate exceeds industry average of 6.2%',
      '2.1M total reach achieved with efficient cost per impression',
      'High-quality content generated positive brand sentiment',
    ],
    weak: [
      'Video completion rates could be improved with shorter content',
      'Peak engagement hours: 7-9 PM, optimize posting schedule',
      'Geographic concentration in urban areas, consider rural expansion',
    ],
  };

  const topCreators = [
    {
      name: 'Amara Fashion',
      title: 'Top Performer',
      stats: [
        '12.4% engagement rate (48% above average)',
        'Strong audience authenticity (94% real followers)',
        'Content resonates well with target demographic',
      ],
    },
    {
      name: 'Style Queen',
      title: 'High Reach',
      stats: ['Highest reach: 340K impressions', 'Strong storytelling and brand integration', 'Consistent posting schedule maintained'],
    },
  ];

  const recommendations = [
    {
      icon: 'lucide:lightbulb',
      title: 'Content Strategy',
      lists: ['Focus on 15-30 second videos', 'Incorporate trending audio', 'Add more user-generated content'],
    },
    {
      icon: 'lucide:users',
      title: 'Creator Mix',
      lists: ['Retain top 3 performers', 'Add 2 micro-influencers', 'Explore lifestyle creators'],
    },
    {
      icon: 'lucide:calendar',
      title: 'Timing & Budget',
      lists: ['Launch in September', 'Increase budget by 20%', '6-week campaign duration'],
    },
  ];

  const doGetReportInsights = async (campaign_id: string, isPublic = false) => {
    try {
      isFetchingInsights.value = true;
      const url = isPublic ? '/reporting/insights_public' : '/reporting/insights';
      const res = await msApi.smePost(url, {
        campaign_id,
      });

      const insightsData = res.data?.insights as Record<string, Record<string, ApiInsightData>> | undefined;

      reportInsights.value = insightsData
        ? Object.entries(insightsData).flatMap(([platform, platformInsights]) =>
            Object.entries(platformInsights).map(([category, { Insight: text, Action: suggestion, CTA: buttonText }]) => ({
              platform: `${platform}`,
              category: `${category}`,
              text,
              suggestion,
              buttonText,
            })),
          )
        : [];

      isFetchingInsights.value = false;
    } catch (error) {
      reportInsights.value = [];
      isFetchingInsights.value = false;
    }
  };

  onMounted(async () => await doGetReportInsights(props.campaign_id));

  // Prevent body scroll when modal is open
  onMounted(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.maxHeight = '100vh';
  });

  onUnmounted(() => {
    document.body.style.overflow = '';
    document.body.style.maxHeight = 'unset';
  });
</script>

<template>
  <Teleport to="body">
    <div class="insights-modal-wrapper flex items-center justify-center" @click.stop="emits('close-modal')">
      <div class="modal-container">
        <div class="modal-header flex items-center justify-between">
          <h3 class="flex items-center gap-8">
            <iconify-icon icon="lucide:brain" />
            AI Campaign Insights
          </h3>

          <button class="close-modal-btn flex items-center justify-center" @click.stop="emits('close-modal')">
            <iconify-icon icon="lucide:x" />
          </button>
        </div>

        <div class="insights-container flex flex-col gap-24">
          <div class="performance-container flex flex-col gap-16">
            <h4 class="flex items-center gap-8">
              <iconify-icon icon="lucide:trending-up" />
              Campaign Performance Analysis
            </h4>
            <div class="strong-performance flex flex-col gap-8">
              <p>✅ Strong Performance Indicators</p>
              <ul class="flex flex-col gap-4">
                <li v-for="performance in campaignPerformanceAnalysis.strong">{{ performance }}</li>
              </ul>
            </div>
            <div class="weak-performance flex flex-col gap-8">
              <p>📈 Areas for Improvement</p>
              <ul class="flex flex-col gap-4">
                <li v-for="performance in campaignPerformanceAnalysis.weak">{{ performance }}</li>
              </ul>
            </div>
          </div>
          <div class="creator-performance-wrapper flex flex-col gap-16">
            <h4 class="flex items-center gap-8">
              <iconify-icon icon="lucide:trophy" />
              Top Creator Performance
            </h4>
            <div class="creator-performance-container flex flex-col gap-24">
              <div class="creator-performance flex flex-col gap-8" v-for="creator in topCreators">
                <div class="creator-bio-container flex items-center gap-16">
                  <div class="text-container flex flex-col gap-4">
                    <span class="creator-name">{{ creator.name }}</span>
                    <span class="creator-title">{{ creator.title }}</span>
                  </div>
                </div>
                <ul class="flex flex-col gap-4">
                  <li v-for="stat in creator.stats">{{ stat }}</li>
                </ul>
              </div>
            </div>
          </div>
          <div class="recommendations-wrapper flex flex-col gap-16">
            <h4 class="flex items-center gap-8">
              <iconify-icon icon="lucide:target" />
              Next Campaign Recommendations
            </h4>
            <div class="recommendations-container flex flex-col gap-24">
              <div
                class="recommendation flex flex-col gap-8"
                v-for="recommendation in recommendations"
                :class="recommendation.title.toLowerCase().split(' ').join('-')"
              >
                <iconify-icon :icon="recommendation.icon" />
                <div class="creator-bio-container flex items-center gap-16">
                  <span class="recommendation-title">{{ recommendation.title }}</span>
                </div>
                <ul class="flex flex-col gap-4">
                  <li v-for="item in recommendation.lists">{{ item }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
  @import url('./styles/ai-insights.scss');
</style>
